import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { EntiteUtilisateurModel } from 'src/models/selectionEntiteUtilisateurModel';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';
import { StatePersistenceService } from 'src/services/state-persistence.service';
import { EEntite } from 'src/enum/eEntite';

@Component({
    selector: 'app-root-selection-entite',
    templateUrl: './root-selection-entite.component.html',
    styleUrls: ['./root-selection-entite.component.scss'],
    imports: [
    FormsModule,
    TranslateModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule
],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        }
    ]
})
export class RootSelectionEntiteComponent implements OnInit {
  EEntite = EEntite;
  searchTerm: string = '';

  entites: EntiteUtilisateurModel[] = [];
  entitesAffichees: EntiteUtilisateurModel[] = [];
  filteredEntities: EntiteUtilisateurModel[] = [];
  isLoading: boolean = false;

  pageIndex: number = 0;
  pageSize: number = 10;
  currentPage: number = 0;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  displayedColumns: string[] = [
    'nomEntite',
    'idUnique',
    'neq'
  ];

  dataSource!: MatTableDataSource<EntiteUtilisateurModel>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    public utilisateurService: UtilisateurService,
    private router: Router,
    private statePersistenceService: StatePersistenceService
  ) {
    this.dataSource = new MatTableDataSource<EntiteUtilisateurModel>([]);
  }

  ngOnInit(): void {
    this.statePersistenceService.clearAllStates();
    this.utilisateurService.entiteUtilisateurSelectionnee = undefined!;

    this.filterEntities();
  }

  filterEntities(): void {
    this.isLoading = true;
    const filtered = this.utilisateurService.selectionEntiteUtilisateur.entiteUtilisateurs
      .filter(entity =>
        entity.entiteNom.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (entity.idUnique?.toLowerCase().includes(this.searchTerm.toLowerCase()) ?? false) ||
        (entity.neq?.toString().includes(this.searchTerm) ?? false)
      );

    const organisation = filtered.find(e => e.entiteId === EEntite.ROOT_ENTITE_ID);
    const autres = filtered.filter(e => e.entiteId !== EEntite.ROOT_ENTITE_ID).sort((a, b) => a.entiteNom.localeCompare(b.entiteNom));
    this.filteredEntities = organisation ? [organisation, ...autres] : autres;

    this.isLoading = false;
    this.totalItems = this.filteredEntities.length;
    this.pageIndex = 0;
    this.paginer();
  }

  paginer(event?: PageEvent) {
    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    }
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.entitesAffichees = this.filteredEntities.slice(startIndex, endIndex);
    this.dataSource.data = this.entitesAffichees;
    this.changeDetectorRef.detectChanges();
  }

  sortDataChange(sortState: Sort) {
    if (!sortState.direction) {
      this.filterEntities();
      return;
    }

    const organisation = this.filteredEntities.find(e => e.entiteId === EEntite.ROOT_ENTITE_ID);
    const autres = this.filteredEntities.filter(e => e.entiteId !== EEntite.ROOT_ENTITE_ID);

    let key: keyof EntiteUtilisateurModel;
    switch (sortState.active) {
      case 'nomEntite':
        key = 'entiteNom';
        break;
      case 'idUnique':
        key = 'idUnique';
        break;
      case 'neq':
        key = 'neq';
        break;
      default:
        key = 'entiteNom';
    }

    const sortedAutres = autres.sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      let result: number;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        result = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
      } else {
        result = (aValue as any) > (bValue as any) ? 1 : (aValue as any) < (bValue as any) ? -1 : 0;
      }
      return sortState.direction === 'asc' ? result : -result;
    });

    this.filteredEntities = organisation ? [organisation, ...sortedAutres] : sortedAutres;
    this.paginer();
  }

  private updatePaginatorLength(): void {
    if (this.paginator) {
      this.paginator.length = this.filteredEntities.length;
      this.totalItems = this.filteredEntities.length;
      this.paginator.page.emit();
      this.changeDetectorRef.detectChanges();
    }
  }


  refreshDataSource(): void {
    this.filteredEntities = this.entites;
    this.dataSource.data = this.filteredEntities;
    this.updatePaginatorLength();
  }

  onSearch(event: any): void {
    this.searchTerm = event.target.value;
    this.currentPage = 1;
    this.filterEntities();
  }

  selectEntity(entity: EntiteUtilisateurModel): void {
    const oldEntityId = this.utilisateurService.entiteUtilisateurSelectionnee?.entiteId;

    if (oldEntityId && oldEntityId !== entity.entiteId) {
      this.statePersistenceService.clearAllStates();
    }

    this.utilisateurService.entiteUtilisateurSelectionnee = entity;
    this.router.navigate(['tableau-de-bord']);
  }
}
